/**
 * One-shot, once per machine: put the Macro page's sections added after
 * SweeterTrade's release of 2026-11-02 (README "Macro: what was added") at the
 * bottom of its saved arrangements, which were seeded before they existed.
 * The page seed is idempotent by name, so an arrangement that already exists
 * never takes a new pane on its own.
 *
 * Three rows go below whatever the arrangement holds: the AI read alone, then
 * the week's releases beside what moves together, then safe havens beside
 * positioning. A root that is already a stack of rows keeps its id and gains
 * the rows; any other root becomes the first row of a new stack and keeps its
 * own id, so the widths saved under it stand. A row's saved height is kept by
 * its place in its stack, so the rows above keep theirs. On the wide
 * arrangement a row nobody dragged starts at 380px, which is too tall for the
 * AI read, so that row is written at the pane's own height (`READ_ROW_PX`)
 * where a drag would have written it; the other two start at 380px.
 *
 * A tree that already holds any of the five is left alone, so a reader who
 * placed them, or removed them after this ran, keeps it so. The flag is this
 * machine's, as each machine holds its own arrangements, and is not synced.
 * The same shape as migrateFundamentalsPictures.ts.
 */
import { rawGet, rawSet } from "@/lib/display/layoutNamespace";
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import type { WsNode, WorkspacePage } from "@/lib/workspace/workspacePages";

const FLAG = "migrate:macro-additions:v1";
const LIST_KEY = "mpp:workspaces:v1";
const NARROW = "macro · verdict";
const WIDE = "macro · verdict · wide";
const PAGE_NAMES = new Set([NARROW, WIDE]);
/** The page's panel: PanelWorkspacePage's `${storageKey}:ws`, as the nested workspace names its row heights. */
const PANEL_ID = "mpp:macro-preview:pw:v1:ws";
const DP_SEP = "\u0000@dp:";
/** The AI read's row on the wide arrangement: the pane's own `rowPx` (components/fred/preview/paneDefs.tsx). */
export const READ_ROW_PX = 220;

/** The five panes, in the rows they take: one, two, two. */
export const MACRO_ADDED_ROWS: ReadonlyArray<readonly string[]> = [["macvAiRead"], ["macvReleases", "macvTogether"], ["macvHavens", "macvPositioning"]];
export const MACRO_ADDED_IDS: string[] = MACRO_ADDED_ROWS.flat();
const ADDED = new Set(MACRO_ADDED_IDS);

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function holdsAny(node: WsNode): boolean {
  if (node.t === "leaf") return node.component != null && ADDED.has(node.component);
  return node.children.some(holdsAny);
}

/**
 * The tree with the new rows at its bottom, the id of the stack that holds
 * them and the AI read's place in it; null when the tree already holds any of
 * them. Pure: `ids` makes the new nodes' ids.
 */
export function withMacroAdditionsBelow(root: WsNode, ids: (prefix: string) => string = nid): { root: WsNode; stackId: string; readRow: number } | null {
  if (holdsAny(root)) return null;
  const rows: WsNode[] = MACRO_ADDED_ROWS.map((row) =>
    row.length === 1
      ? { id: ids("l"), t: "leaf", component: row[0]! }
      : { id: ids("s"), t: "split", dir: "h", children: row.map((c) => ({ id: ids("l"), t: "leaf", component: c })), sizes: row.map(() => 100 / row.length) },
  );
  if (root.t === "split" && root.dir === "v") return { root: { ...root, children: [...root.children, ...rows] }, stackId: root.id, readRow: root.children.length };
  const stackId = ids("s");
  return { root: { id: stackId, t: "split", dir: "v", children: [root, ...rows] }, stackId, readRow: 1 };
}

/**
 * A stack's saved heights with the AI read's row at `READ_ROW_PX`: the rows
 * above keep what they saved, and a row between with nothing saved stays so
 * (null, which reads as a row nobody dragged).
 */
export function heightsWithRead(saved: string | null, readRow: number): string {
  let arr: unknown[] = [];
  try {
    const parsed: unknown = saved ? JSON.parse(saved) : [];
    if (Array.isArray(parsed)) arr = parsed;
  } catch {
    arr = [];
  }
  const out = Array.from({ length: readRow + 1 }, (_, i) => (i < arr.length && typeof arr[i] === "number" ? arr[i] : null));
  out[readRow] = READ_ROW_PX;
  return JSON.stringify(out);
}

const rowKey = (pageId: string, stackId: string) => `mpp:ws:rowh:nested-ws:${PANEL_ID}:${pageId}:${stackId}:v1`;

export function migrateMacroAdditions(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(FLAG) === "1") return;
  } catch {
    return;
  }

  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }

  const pageIds = new Set<string>();
  const wideIds = new Set<string>();
  let poolChanged = false;
  const nextPool = pool.map((page) => {
    if (!page?.root || typeof page.name !== "string" || !PAGE_NAMES.has(page.name.trim().toLowerCase())) return page;
    pageIds.add(page.id);
    const wide = page.name.trim().toLowerCase() === WIDE;
    if (wide) wideIds.add(page.id);
    const next = withMacroAdditionsBelow(page.root);
    if (!next) return page;
    poolChanged = true;
    // The shared tree is what a profile with no tree of its own draws, with the shared row heights.
    if (wide) rawSet(rowKey(page.id, next.stackId), heightsWithRead(rawGet(rowKey(page.id, next.stackId)), next.readRow));
    return { ...page, root: next.root, updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  // Each Display Profile's own tree of those pages, and the shared override: each written where it was read.
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith("mpp:ws-tree:")) keys.push(key);
    }
    for (const key of keys) {
      const [logical, profile] = key.split(DP_SEP);
      const pageId = [...pageIds].find((id) => logical === `mpp:ws-tree:${id}:v1`);
      if (!pageId) continue;
      const raw = rawGet(key);
      if (!raw) continue;
      const root = JSON.parse(raw) as WsNode;
      if (!root || (root.t !== "leaf" && root.t !== "split")) continue;
      const next = withMacroAdditionsBelow(root);
      if (!next) continue;
      rawSet(key, JSON.stringify(next.root));
      if (wideIds.has(pageId)) {
        const heightsKey = `${rowKey(pageId, next.stackId)}${profile != null ? `${DP_SEP}${profile}` : ""}`;
        rawSet(heightsKey, heightsWithRead(rawGet(heightsKey), next.readRow));
      }
    }
  } catch {
    /* the shared tree's change still stands */
  }

  try {
    window.localStorage.setItem(FLAG, "1");
  } catch {
    /* quota: it runs again next time, and finds nothing to do */
  }
}
